import useGetCustomerProjects from '@/api/queries/customers/useGetCustomerProjects';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { CustomerProjectsQuery } from '@/gql/graphql';
import CreateDealModal from '@/ui/components/createDealModal/CreateDealModal';
import { useAccount } from '@/api/rest/users/useAccount';
import {
  AvatarSizeVariant,
  Button,
  ManageableItemsList,
  UserAvatar,
} from '@payaca/ambrosia';
import Address from '@payaca/components/address/Address';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import Tag from '@payaca/components/tag/Tag';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { PermissionGuard } from '../../../components/permissionGuard/PermissionGuard';

type TData = CustomerProjectsQuery['customer']['projects']['items'][0];

const CustomerProjectTable: FC = () => {
  const history = useHistory();
  const { customerId } = useParams<{ customerId: string }>();

  /**
   * TEMPORARY
   * ToDo: remove redux here
   */
  const account = useAccount();
  /**
   * END: TEMPORARY
   */

  const [isShowingCreateProjectModal, setIsShowingCreateProjectModal] =
    useState(false);
  const [searchTermValue, setSearchTermValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState(PAGINATION_ARG_DEFAULTS);

  const { customer, isLoading } = useGetCustomerProjects(
    customerId,
    { searchTerm },
    pagination
  );
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  return (
    <div className="@container">
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Customer projects"
          buttons={
            <PermissionGuard
              renderIfHasPermissions={[DealsPermissions.ADD_DEAL]}
            >
              <Button
                onClick={() => setIsShowingCreateProjectModal(true)}
                size={'sm'}
              >
                Create project
              </Button>
            </PermissionGuard>
          }
        />

        <ManageableItemsList.ActionBar>
          <ManageableItemsList.ActionBar.SearchInput
            value={searchTermValue}
            onChange={setSearchTermValue}
            onChangeTimeout={setSearchTerm}
            aria-label="Search by reference or site address"
          />
        </ManageableItemsList.ActionBar>

        <ManageableItemsList.Table<TData>
          items={customer?.projects.items || []}
          uniqueKey="id"
          isLoading={!customer || isLoading}
          onClickRow={(item) => {
            history.push(`/projects/${item.publicId}`);
          }}
        >
          <ManageableItemsList.Table.Column<TData, 'reference'>
            header="Ref"
            field="reference"
            render={(reference, row) => {
              return <Link to={`/projects/${row.publicId}`}>{reference}</Link>;
            }}
          />
          <ManageableItemsList.Table.Column<TData, 'addresses'>
            header="Site address"
            field="addresses"
            render={(addresses) => {
              if (!addresses.site.length) return null;

              return (
                <>
                  <Address address={addresses.site[0]} />

                  {addresses.site.length > 1 && (
                    <span className="supporting-body ms-2">
                      +{addresses.site.length - 1} more
                    </span>
                  )}
                </>
              );
            }}
          />
          <ManageableItemsList.Table.Column
            header="Updated"
            field="latestActivityAt"
            render={(updatedAt) => {
              return format(
                new Date(updatedAt),
                getInternationalDateFormatByLocale(
                  DateFormats.MID,
                  accountRegionPreferences?.locale
                )
              );
            }}
          />
          <ManageableItemsList.Table.Column<TData, 'totals'>
            header="Value"
            field="totals"
            render={(totals) => {
              return (
                <CurrencyValue
                  value={totals.projectValue.value}
                  currency={totals.projectValue.currency}
                />
              );
            }}
          />
          <ManageableItemsList.Table.Column<TData, 'pipeline'>
            header="Pipeline"
            field="pipeline"
            render={(pipeline) => {
              return (
                <div className="flex items-center gap-1.5">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: pipeline.colour }}
                  />
                  <span>{pipeline.name}</span>
                </div>
              );
            }}
          />
          <ManageableItemsList.Table.Column<TData, 'currentPipelineStage'>
            header="Stage"
            field="currentPipelineStage"
            render={(currentPipelineStage) => {
              return <>{currentPipelineStage.title}</>;
            }}
          />
          <ManageableItemsList.Table.Column<TData, 'tags'>
            header="Tags"
            field="tags"
            className="max-w-lg"
            render={(tags, row) => {
              return (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, i) => {
                    return (
                      <Tag colour={tag.colour || ''} key={`tag-${i}`}>
                        {tag.name}
                      </Tag>
                    );
                  })}
                </div>
              );
            }}
          />
          <ManageableItemsList.Table.Column<TData, 'owner'>
            header="Owner"
            field="owner"
            render={(value) =>
              value && (
                <UserAvatar
                  sizeVariant={AvatarSizeVariant.XS}
                  user={{
                    firstName: value.firstName || '',
                    lastName: value.lastName || '',
                    emailAddress: value.email || '',
                    userColour: value.colour || '',
                    imgSrc: value.avatarUrl || undefined,
                  }}
                  enableUserInfoTooltip
                />
              )
            }
          />
        </ManageableItemsList.Table>

        {customer && (
          <ManageableItemsList.PaginationBar
            type="custom"
            pageSize={customer.projects.limit}
            currentPage={customer.projects.offset / customer.projects.limit + 1}
            totalItems={customer.projects.totalCount}
            onPageChange={(page) => {
              setPagination((s) => ({ ...s, offset: (page - 1) * s.limit }));
            }}
          />
        )}
      </ManageableItemsList>

      {customer && (
        <CreateDealModal
          isOpen={isShowingCreateProjectModal}
          onClose={() => setIsShowingCreateProjectModal(false)}
          initialPipelineId={account.primaryPipelineId}
          initialCustomerId={customer?.id ? +customer.id : null}
        />
      )}
    </div>
  );
};

export default CustomerProjectTable;
